import { PlusCircleOutlined } from '@ant-design/icons';
import { Button, Modal, Input, message, Spin, Select } from 'antd';
import { observer } from 'mobx-react';
import React, { FC, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import CloseLine from '@/assets/images/icons/close-line.react.svg?react';
import CreateTapdEditDisable from '@/assets/images/public/create-tapd-edit-disable.svg';
import CreateTapdEdit from '@/assets/images/public/create-tapd-edit.svg';
import i18n from '@/i18n';
import { getUserId } from '@/infrastructure/auth';
import { getProductId, isHarmonyOS } from '@/infrastructure/product-info';
import { CUSTOM_ACTIONS } from '@/infrastructure/reporter/datong/constant';
import { reportClick } from '@/infrastructure/reporter/datong/report';
import { storageGet, storageSet } from '@/infrastructure/storage-utils';
import { ModalIconType, ModalMsg, ModalType } from '@/presentation/components/modal-msg';
import {
  getInitFields,
  initReporterData,
  initCurrentOwnerData,
  initVersionReportData,
} from '@/presentation/components/tapd';
import {
  getIssueTapdInfos,
  submitToTapd,
  getCustomFields,
  CreateTapdIssueReq,
  getLastCrashStack, deleteIssueTapdInfos,
} from '@/presentation/components/tapd/api';
import { RenderAutoComplete } from '@/presentation/components/tapd/create-tapd-modal/auto-complete';
import styles from '@/presentation/components/tapd/create-tapd-modal/index.module.scss';
import { RenderInput } from '@/presentation/components/tapd/create-tapd-modal/input';
import { RenderSelect } from '@/presentation/components/tapd/create-tapd-modal/select';
import { RenderUserSelect } from '@/presentation/components/tapd/create-tapd-modal/user-select';
import { EditType, formatTapdInfo } from '@/presentation/components/tapd/edit-tapd-modal';
import { getRoleByUser } from '@/presentation/components/user-manage/api';
import { ANR, FREEZE } from '@/presentation/config/name';
import { useStores } from '@/presentation/store';
import { FilterOptions } from '@/proto/alert';
import {
  Zone,
  TapdLinkedState,
  Option,
  ExOption,
} from '@/proto/field';
import { IStatusLinkageRule, ITapdTemplateInfos } from '@/proto/manage';

const SelectOption = Select.Option;
export interface FieldsType {
  name: string | null | undefined;
  label: string | null | undefined;
  value: string | number | null | undefined;
  type: string;
  options?: FilterOptions[] | undefined;
}

export interface StatusRulesType {
  when_tapd: string;
  then_issue: string;
}

export type CustomFieldsType = Omit<FieldsType, 'value'>;

export const getTapdTitleKey = (type: Zone) => {
  const titleKey: Record<string, string> = {
    [Zone.Lag]: i18n.t(' 【卡顿问题】 '),
    [Zone.Launch]: i18n.t(' 【启动问题】 '),
    [Zone.Compliance]: i18n.t(' 【合规问题】 '),
    [Zone.Foom]: i18n.t(' 【FOOM问题】 '),
    [Zone.FoomMalloc]: i18n.t(' 【FOOM内存分配】 '),
    [Zone.JavaLeak]: i18n.t(' 【Java内存泄漏问题】 '),
    [Zone.VcLeak]: i18n.t(' 【VC泄漏问题】 '),
    [Zone.BigPic]: i18n.t(' 【大图分析问题】 '),
    [Zone.BadMalloc]: i18n.t(' 【大块分配内存问题】 '),
    [Zone.Fd]: i18n.t(' 【FD触顶问题】 '),
    [Zone.Anr]: i18n.t(' 【{{type}}问题】 ', { type: isHarmonyOS() ? FREEZE : ANR }),
    [Zone.Crash]: i18n.t(' 【CRASH问题】 '),
    [Zone.Error]: i18n.t(' 【ERROR问题】 '),
  };
  return titleKey[type];
};

export const StatusMap: { [key: string]: string } = {
  [TapdLinkedState.NotRepaired]: i18n.t('未处理'),
  [TapdLinkedState.Recovered]: i18n.t('已处理'),
  [TapdLinkedState.Repairing]: i18n.t('处理中'),
  [TapdLinkedState.Rejected]: i18n.t('拒绝'),
  [TapdLinkedState.Suspended]: i18n.t('挂起'),
};

export const getTapdStatusName = (value: string | null | undefined, statusFields?: FieldsType) => (
  statusFields?.options?.filter(item => item.value === value)?.[0]?.label
);

export const formatFieldsValue = (fields: Record<string, any>[]) => fields.map(item => ({
  name: item.name,
  value: item.value,
  label: item.label,
})).filter(item => item.name !== 'title_prefix');

export const formatCustomFields = (
  customFields: CustomFieldsType[],
  appVersionOptions: ExOption[],
) => customFields.map((field: any) => {
  if (field.name === initVersionReportData.name) {
    const options = appVersionOptions?.reduce?.<ExOption[]>((prev: any[], current: { value: any; }) => {
      const target = prev.find((item: any) => item.value === current.value);
      if (!target) {
        prev.push(current);
      }
      return prev;
    }, []);
    return {
      ...field,
      type: 'autoComplete',
      options,
    };
  }
  if (field.name === initReporterData.name || field.name === initCurrentOwnerData.name) {
    return {
      ...field,
      type: 'userSelect',
    };
  }
  return field;
});

export const getLocalTapd = () => {
  try {
    return JSON.parse(storageGet(`${getProductId()}-tapd`)!);
  } catch (e) {
    return null;
  }
};

export const CreateTapdModal: FC<{
  type: Zone,
  crashId: string,
  exceptionName: string,
  issueKey: string,
  issueUrl: string,
  pageId: string,
  appVersionOptions: Option[] | ExOption[],
  defaultAppVersion?: string,
  defaultProcessor?: string;
  visible: boolean,
  onClose: () => void,
  onChangeId: (workspaceId: string) => void,
  openEidtModal: (editType: EditType, mode: string) => void,
  onSubmit?: () => void,
}> = observer(({
  appVersionOptions,
  type,
  crashId,
  exceptionName,
  issueKey,
  issueUrl,
  pageId,
  defaultProcessor,
  visible,
  onClose,
  onChangeId,
  openEidtModal,
  onSubmit,
}) => {
  const { t } = useTranslation();
  const { tapdList } = useStores().TapdStore;
  const initStatusParams: CreateTapdIssueReq = {
    product_id: '',
    type,
    issue_key: issueKey,
    work_space_id: '',
    field_values: [],
  };
  const [loading, setLoading] = useState(false);
  const [title, setTitle] = useState('');
  const [content, setContent] = useState<string>('');
  const [statusVisible, setStatusVisible] = useState(false);
  const [tapdLists, setTapdLists] = useState<FilterOptions[]>([]);
  const [statusParams, setStatusParams] = useState(initStatusParams);
  const [statusFields, setStatusFields] = useState<FieldsType>();
  const initTitlePrefix = t('【Bugly-质量监控】');
  const [fields, setFields] = useState<FieldsType[]>(getInitFields(appVersionOptions as FilterOptions[]));
  const [relateStatus, setRelateStatus] = useState<IStatusLinkageRule[]>([]);
  const [tapdTemplateInfos, setTapdTemplateInfos] = useState<ITapdTemplateInfos[]>([]);
  const [mode, setMode] = useState<string>('');
  const [isAdmin, setIsAdmin] = useState(false);
  const [isCreator, setIsCreator] = useState(false);
  const handleSelectChange = (val: string | number, key?: string) => {
    setStatusParams({ ...statusParams, [key as string]: val });
    if (onChangeId) onChangeId(val as string);
  };

  const handleChangeMode = (val: string | number) => setMode(val as string);

  const handleSubmit = async () => {
    if (!statusParams.work_space_id) {
      message.warning(t('请选择TAPD项目'));
      return;
    }
    setLoading(true);
    const res = await submitToTapd({
      issueTapds: [{
        issue: {
          UUID: issueKey,
          status: 0,
        },
        tapd: {
          workSpaceID: statusParams.work_space_id,
          fields: [
            ...formatFieldsValue(fields),
            {
              name: 'title',
              label: t('标题'),
              value: `${title}`,
            },
            {
              name: 'description',
              label: t('详细描述'),
              value: t('{{content}}\n<a href=\'{{url}}&tapd=true\'>&gt;&gt;&gt;点击查看详情&lt;&lt;&lt;</a>', { content, url: issueUrl }).replace(/\n/g, '<br>'),
            },
          ],
        },
      }],
    });
    if (res) {
      setStatusVisible(false);
      onClose();
      if (onSubmit) {
        onSubmit();
      }
    }
    setLoading(false);
    reportClick(pageId, CUSTOM_ACTIONS.commonCreateTapdBug, { from: pageId });
  };

  const loadTapdFields = async () => {
    setLoading(true);

    if (crashId) {
      const issueInfo = await getLastCrashStack(crashId);
      const crashDetail = issueInfo.detail.split('\n')?.filter(item => !!item);
      const title = `${getTapdTitleKey(type)}${exceptionName}`;
      setContent(`${crashDetail.join('\n')}\nIssue ID：${issueKey}\n`);
      setTitle(title);
    }

    const customFields = await getCustomFields(statusParams.work_space_id);   // 获取自定义字段
    const newCustomFields = formatCustomFields(customFields, appVersionOptions!);
    const tapdTemplateInfos = await getIssueTapdInfos({   // 获取tapd模版基本信息(含标题前缀)
      productId: getProductId(),
      workSpaceId: statusParams.work_space_id ?? tapdList[0]?.workspaceId,
    });
    setTapdTemplateInfos(tapdTemplateInfos);
    const tapdInfo = tapdTemplateInfos.find(item => item.templateId === mode) ?? tapdTemplateInfos[0];
    setMode(tapdInfo?.templateId ?? '');
    setIsCreator(tapdInfo?.creator === getUserId());
    storageSet(`${getProductId()}-tapd`, JSON.stringify({ mode, work_space_id: statusParams.work_space_id }));
    if (tapdInfo?.statusRules) {
      setRelateStatus(tapdInfo.statusRules);
    } else {
      setRelateStatus([]);
    }
    const statusFields = newCustomFields.filter((item: CustomFieldsType) => item.name === 'status');
    setStatusFields(statusFields?.[0]);
    const info = formatTapdInfo(tapdInfo?.data ?? [], newCustomFields);  // 返回tapd自定义模块对象和tapd前缀
    if (info.newTitlePrefix) {
      setTitle(t => `${info.newTitlePrefix}${t}`);
    } else {
      setTitle(t => `${initTitlePrefix}${t}`);
    }
    if (!!tapdInfo?.data?.length) {
      const newFields = info.newFields.map((item) => {
        if (item.name === initCurrentOwnerData.name) {
          return {
            ...item,
            value: defaultProcessor?.replaceAll(',', ';') || item.value,
          };
        }
        if (item.name === initReporterData.name) {
          return {
            ...item,
            value: item.value || initReporterData.value,
          };
        }
        return item;
      });
      setFields(newFields);
    } else {
      setFields(getInitFields(appVersionOptions as FilterOptions[]));
    }
    setLoading(false);
  };

  const getNewFields = (changeValue: string | number, name?: string | null) => {
    const newFields = fields.map((item: FieldsType) => {
      if (item.name === name) {
        return { ...item, value: changeValue };
      }
      return item;
    });
    setFields(newFields);
  };

  const loadIsAdmin = async () => {
    setLoading(true);
    const isAdmin = await getRoleByUser();
    setIsAdmin(isAdmin);
    setLoading(false);
  };

  const renderItem = (field: FieldsType) => {
    switch (field.type) {
      case 'select':
        return <RenderSelect
          name={field.name}
          label={field.label}
          options={field.options!}
          getNewFields={getNewFields}
          value={field.value}
          handleSelectChange={handleSelectChange}
        />;
      case 'autoComplete':
        return <RenderAutoComplete
          name={field.name}
          label={field.label}
          options={field.options!}
          getNewFields={getNewFields}
          value={field.value}
          handleSelectChange={handleSelectChange}
        />;
      case 'userSelect':
        return <RenderUserSelect
          name={field.name}
          label={field.label}
          getNewFields={getNewFields}
          value={field.value as string}
        />;
      default:
        return <RenderInput
          name={field.name}
          label={field.label}
          value={field.value}
          getNewFields={getNewFields}
        />;
    }
  };

  useEffect(() => {
    setStatusVisible(visible);
    if (visible) {
      const localTapd = getLocalTapd();
      localTapd?.work_space_id && setStatusParams({ ...statusParams, work_space_id: localTapd.work_space_id });
      localTapd?.mode && setMode(localTapd.mode as string);
      loadIsAdmin();
    }
  }, [visible]);

  useEffect(() => {
    const list: FilterOptions[] = [];
    tapdList.forEach((item) => {
      if (item.workspaceName && item.workspaceId) {
        list.push({ label: item.workspaceName, value: item.workspaceId });
      }
    });
    statusParams.product_id = getProductId();
    statusParams.work_space_id = tapdList[0]?.workspaceId || '';
    setTapdLists(list);
  }, [tapdList]);

  useEffect(() => {
    if (statusParams.work_space_id && statusVisible) {
      loadTapdFields();
    }
  }, [statusParams.work_space_id, statusVisible, mode]);

  const deleteTemplate = (item: ITapdTemplateInfos) => {
    setStatusVisible(false);
    ModalMsg({
      type: ModalType.CONFIRM,
      typeIcon: ModalIconType.INFO,
      content: t('你确认要删除缺陷模板 {{name}} ({{id}})吗？', { name: item.templateName, id: item.templateId }),
      cancelText: t('取消'),
      title: t('删除缺陷模板'),
      okText: t('确认'),
      onCancel: () => {
        setStatusVisible(true);
      },
      onOk: () => new Promise<void>((resolve, reject) => {
        deleteIssueTapdInfos({ templateId: item.templateId }).then((rsp) => {
          if (rsp) {
            message.success(t('删除成功!'));
            resolve();
            setTapdTemplateInfos(infos => infos.filter(info => item.templateId !== info.templateId));
            setStatusVisible(true);
          } else {
            reject();
          }
        });
      }),
    });
  };

  const tapdTemplateInfosOptions = () => {
    const userId = getUserId();
    return tapdTemplateInfos.map(item => (
      <SelectOption key={item.templateId} value={item.templateId} label={`${item.templateName} (${item.templateId})`}>
        <div className={styles.templateInfosOptions}>
          <span role="img" aria-label="China">
            {item.templateName} ({item.templateId})
          </span>
          {isAdmin || item.creator === userId ? <CloseLine onClick={(e: {
            preventDefault: () => void;
            stopPropagation: () => void;
          }) => {
            e.preventDefault();
            e.stopPropagation();
            deleteTemplate(item);
          }} className={styles.closeIcon} /> : null}
        </div>
      </SelectOption>));
  };


  return (<>
    <Modal
      visible={statusVisible}
      title={<div style={{
        fontFamily: 'PingFang SC',
        fontWeight: 500,
        fontSize: '16px',
      }}>{t('创建缺陷')}</div>}
      centered
      footer={null}
      onCancel={() => {
        setStatusVisible(false);
        onClose();
      }}
      style={{ minWidth: '1100px' }}
    >
      <Spin spinning={loading}>
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <RenderSelect
            name={'work_space_id'}
            label={t('关联TAPD')}
            options={tapdLists}
            getNewFields={getNewFields}
            value={statusParams.work_space_id ?? tapdList[0]?.workspaceId}
            handleSelectChange={handleSelectChange}
            labelWidth={'100px'}
            renderSelectWidth={'902px'}
            style={{ width: '794px' }}
          />
        </div>
        <div style={{ display: 'flex' }}>
          <RenderSelect
            name={'mode'}
            label={t('缺陷模板')}
            getNewFields={getNewFields}
            value={mode || tapdTemplateInfos[0]?.templateId || ''}
            handleSelectChange={handleChangeMode}
            labelWidth={'100px'}
            renderSelectWidth={'620px'}
            style={{ width: '510px' }}
            selectProps={{
              optionLabelProp: 'label',
            }}
          >
            {tapdTemplateInfosOptions()}
          </RenderSelect>
          <Button
            style={{
              color: (!isAdmin && !isCreator) || !tapdTemplateInfos.length ? '#D1D1D4' : '#1573ff',
              textAlign: 'center',
              fontSize: '16px',
              lineHeight: '20px',
              marginLeft: '12px',
            }}
            disabled={(!isAdmin && !isCreator) || !tapdTemplateInfos.length}
            onClick={() => {
              if (!statusParams.work_space_id) {
                message.warning(t('请选择TAPD项目，再编辑模板'));
                return;
              }
              setStatusVisible(false);
              onClose();
              openEidtModal('update', mode || tapdTemplateInfos[0]?.templateId);
              onChangeId(statusParams.work_space_id);
            }}>
            <img src={(!isAdmin && !isCreator) || !tapdTemplateInfos.length ? CreateTapdEditDisable : CreateTapdEdit} style={{ marginRight: '12px' }} alt={''} />
            {t('编辑模板')}
          </Button>
          <Button
            style={{
              color: '#0A6CFF',
              textAlign: 'center',
              fontSize: '16px',
              lineHeight: '20px',
              marginLeft: '12px',
            }}
            onClick={() => {
              if (!statusParams.work_space_id) {
                message.warning(t('请选择TAPD项目，再创建新模板'));
                return;
              }
              setStatusVisible(false);
              onClose();
              openEidtModal('add', '');
              onChangeId(statusParams.work_space_id);
            }}>
            <PlusCircleOutlined style={{ marginRight: '1px' }} />
            {t('创建新模板')}
          </Button>
        </div>
        <div style={{ display: 'flex' }}>
          <span style={{ flexShrink: 0, lineHeight: '30px', marginRight: '12px', width: '100px', textAlign: 'right' }}>{t('标题')}</span>
          <Input
            value={`${title}`}
            onChange={e => setTitle(e.target.value)}
          />
        </div>
        <div style={{ marginBottom: '20px', marginTop: '40px', fontSize: '16px', fontWeight: 500, fontFamily: 'PingFang SC' }}>
          {t('基本信息')}
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap' }}>
          {fields.map(field => renderItem(field))}
        </div>
        <div style={{
          marginBottom: '15px',
          fontSize: '14px',
          fontWeight: 500,
          marginTop: '20px',
          fontFamily: 'PingFang SC',
          // 联动规则不显示，功能暂时不下线
          display: 'none',
        }}>
          {t('Issue处理状态联动')}
        </div>
        <div style={{
          // display: 'flex',
          alignItems: 'flex-end',
          // 联动规则不显示，功能暂时不下线
          display: 'none',
        }}>
          <div>
            {relateStatus.length ? relateStatus.map(item => (
              <div>{t('当TAPD状态为')}<span style={{
                fontWeight: 600,
                display: 'inline-block',
                padding: '0 3px',
              }}>{getTapdStatusName(item.whenTapd, statusFields)}</span>{t('时，Issue状态更改为')}<span style={{
                fontWeight: 600,
                display: 'inline-block',
                padding: '0 3px',
              }}>{item.thenIssue ? StatusMap[item.thenIssue] : ''}</span></div>
            )) : <div>{t('未配置规则')}</div>}
          </div>
          <span
            style={{
              cursor: (!isAdmin && !isCreator) || !tapdTemplateInfos.length ? 'not-allowed' : 'pointer',
              color: (!isAdmin && !isCreator) || !tapdTemplateInfos.length ? '#D1D1D4' : '#1573ff',
              marginLeft: '10px',
            }}
            onClick={() => {
              if ((!isAdmin && !isCreator) || !tapdTemplateInfos.length) {
                return;
              }
              if (!statusParams.work_space_id) {
                message.warning(t('请选择TAPD项目，再编辑模板'));
                return;
              }
              setStatusVisible(false);
              onClose();
              openEidtModal('update', mode || tapdTemplateInfos[0]?.templateId);
              onChangeId(statusParams.work_space_id);
            }}>{t('编辑规则')}</span>
        </div>
        <div>
          <div style={{ marginBottom: '15px', fontSize: '14px', fontWeight: 500, fontFamily: 'PingFang SC', marginTop: '30px' }}>{t('缺陷内容')}</div>
          <Input.TextArea
            value={content}
            style={{ minHeight: '200px' }}
            onChange={(e) => {
              setContent(e.target.value);
            }}
          />
        </div>
        <div style={{ textAlign: 'right', marginTop: '40px' }}>
          <Button onClick={() => {
            setStatusVisible(false);
            onClose();
          }}>
            {t('取消')}
          </Button>
          <Button type='primary' onClick={handleSubmit} style={{ marginLeft: '20px' }}>
            {t('确定')}
          </Button>
        </div>
      </Spin>
    </Modal>
  </>
  );
});
